"use client";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LinkService from "@/app/_services/LinkService";
import { toast } from "@/app/_utils/toast";
import type { Link, LinkPreview } from "../linksPageModel";

export function useLinkDetailModel() {
  const { linkId } = useParams<{ linkId: string }>();
  const navigate = useNavigate();
  const [link, setLink] = useState<Link | null>(null);
  const [preview, setPreview] = useState<LinkPreview | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  async function loadLink() {
    if (!linkId) return;
    setIsLoading(true);
    try {
      setLink(await LinkService.getLinkAsync(linkId));
    } finally {
      setIsLoading(false);
    }
    // The resolved share card is a separate call so the Links list endpoint
    // doesn't take a show lookup per row. Failure here only blanks the preview
    // card, so it must not block the page.
    try {
      setPreview(await LinkService.getLinkPreviewAsync(linkId));
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    loadLink();
  }, [linkId]);

  async function handleDelete() {
    if (!link) return;
    try {
      const res = await LinkService.deleteLinkAsync(link.id);
      if (!res.ok) throw new Error(`Delete failed with ${res.status}`);
      toast.success("Link deleted");
      navigate("/admin/marketing/links");
    } catch (e) {
      toast.error("Failed to delete link");
    }
  }

  return {
    link,
    preview,
    isLoading,
    refetch: loadLink,
    handleDelete,
  };
}
